import { API_ERROR_HTTP_STATUS, parseApiErrorResponse } from "@/api/apiError";

export type AuthTokenProvider = () => Promise<string | null> | string | null;

/**
 * Asks for a new token regardless of the local clock, or reports null when
 * there is no session left to refresh.
 */
export type AuthTokenRefresher = () => Promise<string | null>;

function getHeaders(optionsHeaders: RequestInit["headers"], authToken?: string | null) {
  const headers = new Headers(optionsHeaders);
  if (authToken) {
    headers.set("Authorization", `Bearer ${authToken}`);
  }

  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (supabaseAnonKey) {
    headers.set("apikey", supabaseAnonKey);
  }

  return headers;
}

export async function apiRequest<T>(
  url: string,
  options: RequestInit,
  getAuthToken?: AuthTokenProvider,
  refreshAuthToken?: AuthTokenRefresher,
): Promise<T> {
  const authToken = await getAuthToken?.();
  const res = await fetch(url, { ...options, headers: getHeaders(options.headers, authToken) });
  if (res.ok) {
    return res.json() as Promise<T>;
  }

  const error = await parseApiErrorResponse(res);

  // A 401 can mean the local clock still rates the token valid while the server does not.
  if (res.status !== API_ERROR_HTTP_STATUS.UNAUTHORIZED || !refreshAuthToken) {
    throw error;
  }

  const refreshedAuthToken = await refreshAuthToken();
  if (!refreshedAuthToken || refreshedAuthToken === authToken) {
    throw error;
  }

  const retryRes = await fetch(url, { ...options, headers: getHeaders(options.headers, refreshedAuthToken) });
  if (!retryRes.ok) {
    throw await parseApiErrorResponse(retryRes);
  }

  return retryRes.json() as Promise<T>;
}
